import restClient from 'config/httpClient';
import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { sendMessageSuccess } from 'modules/chat/actions';
import { createNotification } from 'modules/notifications/actions';
import { getSignedUploadUrl, uploadMedia } from 'services/api/MediaService';
import { getUnsignedDocuments } from 'modules/entity/selectors/global';
import {
  dismissedSignatureDocumentIds as dismissedSignatureDocumentIdsSelector,
  isSignHereModalOpen,
} from 'modules/entity/selectors';
import { INTERVENTION_UPDATE_FROM_SOCKET } from 'modules/websockets/actionTypes';
import { getLocalStorageItem, removeLocalStorageItem } from 'utils/localStorage';
import { callMeBackSuccess, sendOpinionSuccess } from 'modules/entity/subEntities/intervention/actions/general';
import { answerWorksSuccess } from 'modules/entity/subEntities/intervention/actions/works';

import { PENDING_SIGNATURE_STORAGE_KEY } from '../constants';
import { SIGN_DOCUMENTS } from '../actionTypes';
import {
  closeSignatureModal,
  closeSignHereModal,
  fetchEntity,
  markSignatureDocumentsDismissed,
  openSignatureModal,
  sendRatingSuccess,
  setDocumentSignatureLoading,
  signDocumentsFailure,
} from '../actions';

const SIGNATURE_MODAL_TRACKED_ACTIONS = [
  sendOpinionSuccess,
  callMeBackSuccess,
  answerWorksSuccess,
  sendMessageSuccess,
  sendRatingSuccess,
];

const hasUndismissedDocuments = (unsignedDocuments, dismissedIds) =>
  unsignedDocuments.length > 0 && unsignedDocuments.some(({ id }) => !dismissedIds.includes(id));

export function* handleSignatureAction(action) {
  const unsignedDocuments = yield select(getUnsignedDocuments);

  if (closeSignatureModal.match(action)) {
    yield put(markSignatureDocumentsDismissed(unsignedDocuments.map(({ id }) => id)));
    return;
  }

  const dismissedIds = yield select(dismissedSignatureDocumentIdsSelector);
  if (hasUndismissedDocuments(unsignedDocuments, dismissedIds)) {
    yield put(openSignatureModal());
  }
}

export function* watchSignatureModalTrigger() {
  yield takeEvery([...SIGNATURE_MODAL_TRACKED_ACTIONS, closeSignatureModal], handleSignatureAction);
}

export function* waitForSignatureFlow() {
  const unsignedDocuments = yield select(getUnsignedDocuments);
  const dismissedIds = yield select(dismissedSignatureDocumentIdsSelector);
  if (hasUndismissedDocuments(unsignedDocuments, dismissedIds)) {
    // isSignatureModalOpen/isSignHereModalOpen also close on INTERVENTION_UPDATE_FROM_SOCKET
    // (see reducers/index.js) without ever dispatching closeSignatureModal/closeSignHereModal —
    // wait on that too, or this would block forever when a socket update force-closes the modal.
    yield take([closeSignatureModal, INTERVENTION_UPDATE_FROM_SOCKET]);
    const isSignHereOpen = yield select(isSignHereModalOpen);
    if (isSignHereOpen) {
      yield take([closeSignHereModal, INTERVENTION_UPDATE_FROM_SOCKET]);
    }
  }
}

export function* checkPendingSignatureModalOnLoad(entityId) {
  const pendingSignatureEntityId = getLocalStorageItem(PENDING_SIGNATURE_STORAGE_KEY);
  if (pendingSignatureEntityId && pendingSignatureEntityId === entityId) {
    removeLocalStorageItem(PENDING_SIGNATURE_STORAGE_KEY);
    const unsignedDocuments = yield select(getUnsignedDocuments);
    if (unsignedDocuments?.length > 0) {
      yield put(openSignatureModal());
    }
  }
}

function* onInterventionUpdate({ payload }) {
  if (payload.type === 'DOCUMENT_SIGNED') {
    yield put(fetchEntity({ refresh: true }));
    yield put(setDocumentSignatureLoading(false));
  }
}

// Success has no dispatch of its own: the signing job completes asynchronously server-side, and
// `setDocumentSignatureLoading(false)` only comes back through the DOCUMENT_SIGNED socket event
// above. A failure, however, resets it immediately below — the signing job never started server-side.
export function* signDocuments({ payload }) {
  const { signUploadUrl, signDocumentUrl, image, documentIds } = payload;

  try {
    const { uploadId, url, fields } = yield call(getSignedUploadUrl, signUploadUrl, {
      contentType: 'image/png',
      filename: 'signature.png',
    });
    yield call(uploadMedia, image, url, fields);
    yield call(restClient.post, signDocumentUrl, { uploadId, documentIds });
    yield put(
      createNotification({
        type: 'success',
        message: { id: 'web.documents.sign.success', defaultMessage: 'All documents have been properly signed.' },
      })
    );
  } catch (error) {
    yield put(setDocumentSignatureLoading(false));
    yield put(signDocumentsFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: { id: 'web.error.file.common', defaultMessage: 'An error has occurred.' },
      })
    );
  }
}

export default function* signatureModalSagas() {
  yield takeLatest(INTERVENTION_UPDATE_FROM_SOCKET, onInterventionUpdate);
  yield takeLatest(SIGN_DOCUMENTS, signDocuments);
}
